import { useState, type FormEvent } from 'react';
import { CalendarClock, Plus, ShieldCheck, Trash2, UserRoundCog, X } from 'lucide-react';
import type { FamilyLocation, FamilyProfile, GroupMeetup } from '../types';

interface AdminSheetProps {
  profiles: FamilyProfile[];
  locations: FamilyLocation[];
  groupMeetup: GroupMeetup | null;
  isBusy: boolean;
  onClose: () => void;
  onMovePerson: (profileId: string, locationId: string) => Promise<void>;
  onAddLocation: (name: string, emoji: string) => Promise<void>;
  onCreateMeetup: (locationId: string, scheduledFor: string) => Promise<void>;
  onCancelMeetup: () => Promise<void>;
}

function defaultMeetupTime() {
  const date = new Date(Date.now() + 30 * 60_000);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function AdminSheet({ profiles, locations, groupMeetup, isBusy, onClose, onMovePerson, onAddLocation, onCreateMeetup, onCancelMeetup }: AdminSheetProps) {
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationEmoji, setNewLocationEmoji] = useState('📍');
  const [meetupLocationId, setMeetupLocationId] = useState(locations[0]?.id || '');
  const [meetupTime, setMeetupTime] = useState(defaultMeetupTime);

  async function handleAddLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onAddLocation(newLocationName.trim(), newLocationEmoji.trim() || '📍');
    setNewLocationName('');
    setNewLocationEmoji('📍');
  }

  async function handleCreateMeetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onCreateMeetup(meetupLocationId, new Date(meetupTime).toISOString());
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="admin-sheet" role="dialog" aria-modal="true" aria-labelledby="admin-title">
        <div className="sheet-handle" aria-hidden="true" />
        <header className="admin-sheet__header">
          <div>
            <p className="eyebrow"><ShieldCheck size={15} /> Solo amministratore</p>
            <h2 id="admin-title">Gestisci L.O.C.U.S.</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Chiudi pannello admin"><X /></button>
        </header>

        <section className="admin-section" aria-labelledby="move-people-title">
          <h3 id="move-people-title"><UserRoundCog size={19} /> Sposta una persona</h3>
          <p>La famiglia riceverà una notifica anche quando sei tu ad aggiornarla.</p>
          <ul className="admin-people-list">
            {profiles.map((profile) => (
              <li key={profile.id}>
                <label>
                  <span>{profile.display_name}{profile.is_admin ? ' · Admin' : ''}</span>
                  <select
                    value={profile.current_location_id || ''}
                    disabled={isBusy}
                    onChange={(event) => void onMovePerson(profile.id, event.target.value)}
                    aria-label={`Posizione di ${profile.display_name}`}
                  >
                    <option value="" disabled>Non impostata</option>
                    {locations.map((location) => <option key={location.id} value={location.id}>{location.emoji} {location.name}</option>)}
                  </select>
                </label>
              </li>
            ))}
          </ul>
        </section>

        <section className="admin-section" aria-labelledby="add-location-title">
          <h3 id="add-location-title"><Plus size={19} /> Aggiungi un luogo</h3>
          <form className="add-location-form" onSubmit={handleAddLocation}>
            <label className="field field--emoji">
              <span>Icona</span>
              <input value={newLocationEmoji} maxLength={4} onChange={(event) => setNewLocationEmoji(event.target.value)} />
            </label>
            <label className="field field--grow">
              <span>Nome</span>
              <input value={newLocationName} maxLength={32} required placeholder="Es. Sala giochi" onChange={(event) => setNewLocationName(event.target.value)} />
            </label>
            <button className="button button--primary add-location-form__submit" type="submit" disabled={isBusy || !newLocationName.trim()}>
              Aggiungi
            </button>
          </form>
        </section>

        <section className="admin-section" aria-labelledby="meetup-title">
          <h3 id="meetup-title"><CalendarClock size={19} /> Ritrovo di gruppo</h3>
          <p>Imposta luogo e orario: comparirà un banner e partirà una notifica per la famiglia.</p>
          {groupMeetup ? (
            <div className="active-meetup-admin">
              <strong>{groupMeetup.location_emoji} {groupMeetup.location_name}</strong>
              <span>{new Intl.DateTimeFormat('it-IT', { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(groupMeetup.scheduled_for))}</span>
              <button className="button button--danger" type="button" disabled={isBusy} onClick={() => void onCancelMeetup()}><Trash2 size={17} /> Annulla ritrovo</button>
            </div>
          ) : (
            <form className="meetup-form" onSubmit={handleCreateMeetup}>
              <label className="field">
                <span>Luogo</span>
                <select value={meetupLocationId} required onChange={(event) => setMeetupLocationId(event.target.value)}>
                  {locations.map((location) => <option key={location.id} value={location.id}>{location.emoji} {location.name}</option>)}
                </select>
              </label>
              <label className="field">
                <span>Giorno e ora</span>
                <input type="datetime-local" value={meetupTime} required onChange={(event) => setMeetupTime(event.target.value)} />
              </label>
              <button className="button button--primary" type="submit" disabled={isBusy || !meetupLocationId || !meetupTime}>Avvisa la famiglia</button>
            </form>
          )}
        </section>
      </section>
    </div>
  );
}
